import {useState} from "react";
import axios from "axios";
import {BASE_OWNER_URL} from "@shared/config/base-url";
import {CommonDto} from "@shared/types/common-dto";
import {SignInType} from "@entities/auth/types/login-types";
import {LocalStorageKey} from "@shared/config/local-storage-key";
import {LOGIN_OK} from "@shared/config/constants";
import {clearSession} from "@shared/utils/clear-session";
import { extractApiError } from "@shared/lib/api/extract-api-error";

const defaultErrorMessage = "An unexpected error occurred.";

/**
 * 토큰 재발급 결과.
 * - SUCCESS: 재발급 성공(새 토큰 저장 완료)
 * - AUTH_FAILED: 세션 무효(리프레시 없음/만료/success:false) → 호출자가 로그아웃 처리
 * - ERROR: 일시적 오류(서버 500/네트워크) → 세션 유지, 호출자가 판단(강제 로그아웃 금지)
 */
export type ReissueResult = "SUCCESS" | "AUTH_FAILED" | "ERROR";

export const useReissueApi = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 부수효과(navigate)는 호출자가 결과를 보고 결정한다. 여기서는 재발급 + 토큰 저장만.
    const reissueApi = async (): Promise<ReissueResult> => {
        const refreshToken = localStorage.getItem(LocalStorageKey.RE_AUTHORIZATION);

        // 리프레시 토큰 없음 → 세션 무효
        if (!refreshToken) {
            clearSession();
            return "AUTH_FAILED";
        }

        setLoading(true);
        setError(null);

        try {
            const response = await axios.patch<CommonDto<SignInType>>(
                `${BASE_OWNER_URL}/api/v3/accounts/reissue-tokens`,
                undefined,
                {
                    headers: {
                        Authorization: `Bearer ${refreshToken}`,
                    },
                }
            );

            const apiResponse = response.data;
            const accessToken = apiResponse.data?.accessToken;
            const newRefreshToken = apiResponse.data?.refreshToken;

            if (apiResponse.success && accessToken && newRefreshToken) {
                localStorage.setItem(LocalStorageKey.AUTHORIZATION, accessToken);
                localStorage.setItem(LocalStorageKey.RE_AUTHORIZATION, newRefreshToken);
                localStorage.setItem(LocalStorageKey.LOGIN_STATE, LOGIN_OK);

                return "SUCCESS";
            }

            // API 가 success:false → 세션 무효
            clearSession();
            return "AUTH_FAILED";
        } catch (e: any) {
            const status = e.response?.status;

            if (status === 401) {
                // 리프레시 만료 → 세션 무효
                clearSession();
                return "AUTH_FAILED";
            }

            // 일시적 오류(500/네트워크 등) → 세션은 유지(강제 로그아웃 금지). 실제 인증 실패는 인터셉터가 처리.
            setError(extractApiError(e, defaultErrorMessage));
            return "ERROR";
        } finally {
            setLoading(false);
        }
    };

    return {reissueApi, loading, error};
};
